import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { AuditCell } from "@/components/AuditCell";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

type Supplier = components["schemas"]["SupplierResponse"];

interface SuppliersTableProps {
  organisationId?: string;
}

export function SuppliersTable({ organisationId }: SuppliersTableProps) {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput);

  const pagination = useCursorPagination({ initialLimit: 20 });

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const prevSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      prevSearchRef.current = debouncedSearch;
      pagination.reset();
    }
  }, [debouncedSearch, pagination.reset]);

  useEffect(() => {
    let cancelled = false;

    async function fetchSuppliers() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/suppliers", {
        params: {
          query: {
            OrganisationId: organisationId,
            Name: debouncedSearch || undefined,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load suppliers.");
      } else {
        setSuppliers(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchSuppliers();
    return () => {
      cancelled = true;
    };
  }, [organisationId, pagination.cursor, pagination.limit, debouncedSearch]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Input
          placeholder="Filter by name..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="max-w-sm"
        />
        {loading && <Spinner className="size-5" />}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>ID</TableHead>
              {!organisationId && <TableHead>Organisation</TableHead>}
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Last modified</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {suppliers.length === 0 && !loading ? (
              <TableRow>
                <TableCell
                  colSpan={organisationId ? 5 : 6}
                  className="text-muted-foreground h-24 text-center"
                >
                  No suppliers found.
                </TableCell>
              </TableRow>
            ) : (
              suppliers.map((supplier) => (
                <TableRow key={supplier.id}>
                  <TableCell className="font-medium">
                    {supplier.id ? (
                      <Link to={`/suppliers/${supplier.id}`} className="hover:underline">
                        {supplier.name}
                      </Link>
                    ) : (
                      supplier.name
                    )}
                  </TableCell>
                  <TableCell>
                    {supplier.id ? (
                      <Link to={`/suppliers/${supplier.id}`}>
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {supplier.id.slice(0, 8)}...
                        </Badge>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  {!organisationId && (
                    <TableCell>
                      {supplier.organisationId ? (
                        <Link to={`/organisations/${supplier.organisationId}`}>
                          <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                            {supplier.organisationId.slice(0, 8)}...
                          </Badge>
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    {supplier.email || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    {supplier.phone || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    <AuditCell
                      createdAt={supplier.createdAt ?? ""}
                      createdBy={supplier.createdBy}
                      updatedAt={supplier.updatedAt ?? ""}
                      updatedBy={supplier.updatedBy}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
